<template>
  <div class="sc-page">
    <!-- ---------- HERO ---------- -->
    <section class="sc-hero">
      <img
        :src="require('@/assets/stellar-collect/feature-graphic.png')"
        :alt="t.sc_hero_alt"
        class="sc-hero-img"
        loading="eager" />
      <div class="sc-hero-overlay">
        <img
          :src="require('@/assets/stellar-collect/icon.png')"
          :alt="t.sc_icon_alt"
          class="sc-icon"
          loading="eager" />
        <span class="sc-studio">Club Baby Pro</span>
        <h1 class="sc-title">Stellar Collect</h1>
        <p class="sc-subtitle">{{ t.sc_subtitle }}</p>
        <a
          class="gplay-badge"
          :href="playStoreUrl"
          target="_blank" rel="noopener noreferrer">
          <img
            :alt="t.sc_download_sub"
            src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" />
        </a>
      </div>
    </section>

    <!-- ---------- PITCH ---------- -->
    <section class="sc-intro">
      <b-container>
        <p class="sc-pitch" v-reveal>{{ t.sc_pitch }}</p>
        <ul class="sc-facts" v-reveal>
          <li><strong>Android</strong><span>{{ t.sc_fact_android }}</span></li>
          <li><strong>{{ t.sc_fact_genre }}</strong><span>{{ t.sc_fact_genre_sub }}</span></li>
          <li><strong>{{ t.sc_fact_ranks }}</strong><span>{{ t.sc_fact_ranks_sub }}</span></li>
          <li><strong>{{ t.sc_fact_languages }}</strong><span>{{ t.sc_fact_localized }}</span></li>
        </ul>
      </b-container>
    </section>

    <!-- ---------- LE TAP ---------- -->
    <section class="section">
      <b-container>
        <div class="sc-feature" v-reveal>
          <div class="sc-shot-wrap">
            <img
              :src="require('@/assets/stellar-collect/screenshot-1.png')"
              :alt="t.sc_shot_game_alt"
              class="sc-shot"
              loading="lazy" />
          </div>
          <div class="sc-feature-text">
            <span class="eyebrow">{{ t.sc_tap_eyebrow }}</span>
            <h2 class="sc-section-title">{{ t.sc_tap_title }}</h2>
            <p>{{ t.sc_tap_text }}</p>
          </div>
        </div>
      </b-container>
    </section>

    <!-- ---------- LE PILOTAGE ---------- -->
    <section class="section sc-band">
      <b-container>
        <div class="sc-content card-cbp" v-reveal>
          <span class="eyebrow">{{ t.sc_pilot_eyebrow }}</span>
          <h2 class="sc-section-title">{{ t.sc_pilot_title }}</h2>
          <p>{{ t.sc_pilot_text }}</p>
        </div>
      </b-container>
    </section>

    <!-- ---------- PROGRESSION ---------- -->
    <section class="section">
      <b-container>
        <div class="sc-feature sc-feature-reverse" v-reveal>
          <div class="sc-feature-text">
            <span class="eyebrow">{{ t.sc_progress_eyebrow }}</span>
            <h2 class="sc-section-title">{{ t.sc_progress_title }}</h2>
            <p>{{ t.sc_progress_text }}</p>
          </div>
          <div class="sc-shot-trio">
            <img
              :src="require('@/assets/stellar-collect/screenshot-2.png')"
              :alt="t.sc_shot_upgrades_alt"
              class="sc-shot"
              loading="lazy" />
            <img
              :src="require('@/assets/stellar-collect/screenshot-3.png')"
              :alt="t.sc_shot_garage_alt"
              class="sc-shot"
              loading="lazy" />
            <img
              :src="require('@/assets/stellar-collect/screenshot-4.png')"
              :alt="t.sc_shot_ranks_alt"
              class="sc-shot"
              loading="lazy" />
          </div>
        </div>
      </b-container>
    </section>

    <!-- ---------- HORS LIGNE ---------- -->
    <section class="section sc-band">
      <b-container>
        <div class="sc-content card-cbp" v-reveal>
          <span class="eyebrow">{{ t.sc_idle_eyebrow }}</span>
          <h2 class="sc-section-title">{{ t.sc_idle_title }}</h2>
          <p>{{ t.sc_idle_text }}</p>
        </div>
      </b-container>
    </section>

    <!-- ---------- SERVICES ---------- -->
    <section class="section sc-services-section">
      <b-container>
        <div class="sc-services" v-reveal>
          <h2 class="sc-services-title">{{ t.sc_services_title }}</h2>
          <ul class="sc-services-list">
            <li v-for="item in t.sc_services_items" :key="item">{{ item }}</li>
          </ul>
          <p>{{ t.sc_services_note }}</p>
        </div>
      </b-container>
    </section>

    <!-- ---------- TELECHARGEMENT ---------- -->
    <section class="sc-actions">
      <b-container>
        <div class="sc-actions-inner" v-reveal>
          <h2 class="sc-download-title">{{ t.sc_download_title }}</h2>
          <p class="sc-download-sub">{{ t.sc_download_sub }}</p>
          <div class="sc-buttons">
            <a
              class="gplay-badge"
              :href="playStoreUrl"
              target="_blank" rel="noopener noreferrer">
              <img
                :alt="t.sc_download_sub"
                src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png" />
            </a>
            <a class="btn-quiet" href="/stellar-collect/privacy.html">
              {{ t.sc_privacy_link }}
            </a>
          </div>
        </div>
      </b-container>
    </section>
  </div>
</template>

<script>
import en from '@/en'
import fr from '@/fr'

const PACKAGE_NAME = 'com.clubbabypro.stellarcollect'

export default {
  name: 'StellarCollectGame',
  data () {
    return {
      playStoreUrl: `https://play.google.com/store/apps/details?id=${PACKAGE_NAME}&pcampaignid=pcampaignidMKT-Other-global-all-co-prtnr-py-PartBadge-Mar2515-1`,
      languages: {
        en,
        fr
      }
    }
  },
  computed: {
    language () {
      return this.$store.state.language
    },
    t () {
      return this.languages[this.language]
    }
  },
  // Donnees structurees : Google peut alors presenter la page comme un jeu
  // (nom, plateforme, prix) et non comme une page quelconque.
  mounted () {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'VideoGame',
      name: 'Stellar Collect',
      applicationCategory: 'GameApplication',
      gamePlatform: 'Android',
      operatingSystem: 'Android',
      inLanguage: ['fr', 'en', 'es'],
      author: { '@type': 'Organization', name: 'Club Baby Pro' },
      publisher: { '@type': 'Organization', name: 'Club Baby Pro' },
      description: this.t.sc_card_text,
      installUrl: this.playStoreUrl,
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' }
    }
    const tag = document.createElement('script')
    tag.type = 'application/ld+json'
    tag.id = 'sc-schema'
    tag.textContent = JSON.stringify(schema)
    document.head.appendChild(tag)
  },
  beforeDestroy () {
    const tag = document.getElementById('sc-schema')
    if (tag) {
      tag.remove()
    }
  }
}
</script>

<style scoped lang="scss">
  .sc-page {
    --sc-violet: #7c3aed;
    --sc-cyan: #38bdf8;
    --sc-deep: #150c2b;
  }

  /* ---------- HERO ---------- */
  .sc-hero {
    position: relative;
    min-height: clamp(360px, 58vw, 640px);
    overflow: hidden;
    border-bottom: 1px solid var(--border);
    background: var(--sc-deep);
  }

  .sc-hero-img {
    display: block;
    width: 100%;
    height: clamp(360px, 58vw, 640px);
    object-fit: cover;
  }

  .sc-hero-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    padding: clamp(1.25rem, 4vw, 3rem) 1rem;
    text-align: center;
    color: #fff;
    background: linear-gradient(to top, rgba(12, 6, 26, 0.94) 0%, rgba(12, 6, 26, 0.55) 40%, rgba(12, 6, 26, 0.05) 100%);
  }

  .sc-icon {
    width: clamp(72px, 12vw, 120px);
    height: clamp(72px, 12vw, 120px);
    object-fit: cover;
    border-radius: 24%;
    border: 3px solid rgba(255, 255, 255, 0.88);
    box-shadow: 0 18px 35px rgba(10, 5, 24, 0.55);
    margin-bottom: 0.9rem;
  }

  .sc-studio {
    font-family: 'agency', sans-serif;
    text-transform: uppercase;
    letter-spacing: 5px;
    color: var(--sc-cyan);
    font-size: clamp(0.85rem, 2vw, 1.2rem);
  }

  .sc-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(2.7rem, 9vw, 6.2rem);
    margin: 0.15rem 0 0.6rem;
    text-shadow: 0 5px 30px rgba(0, 0, 0, 0.6);
  }

  .sc-subtitle {
    max-width: 52ch;
    margin: 0 0 1.25rem;
    font-family: 'agency', sans-serif;
    font-size: clamp(1.05rem, 2.4vw, 1.45rem);
  }

  /* ---------- PITCH ---------- */
  .sc-intro {
    padding: clamp(2.5rem, 6vw, 4.5rem) 0 0;
    text-align: center;
  }

  .sc-pitch {
    max-width: 66ch;
    margin: 0 auto clamp(2rem, 4vw, 3rem);
    font-size: clamp(1.05rem, 2vw, 1.35rem);
    color: var(--text);
    font-family: 'agency', sans-serif;
  }

  .sc-facts {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 1rem;
  }
  .sc-facts li {
    flex: 1 1 180px;
    max-width: 240px;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    box-shadow: var(--shadow-card);
    padding: 1.1rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .sc-facts strong {
    font-family: 'fightt', sans-serif;
    font-size: clamp(1.3rem, 3vw, 1.8rem);
    color: var(--sc-violet);
    line-height: 1.1;
  }
  .sc-facts span {
    color: var(--text-muted);
    font-size: 0.95rem;
  }

  /* ---------- SECTIONS ---------- */
  .sc-section-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(1.9rem, 4.4vw, 2.9rem);
    margin-bottom: 0.9rem;
  }

  .sc-feature {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    align-items: center;
    gap: clamp(1.5rem, 4vw, 3.5rem);
  }
  .sc-feature-reverse {
    grid-template-columns: 1.1fr 0.9fr;
  }
  .sc-feature-text p {
    color: var(--text-muted);
    margin: 0;
    font-size: 1.05rem;
  }

  .sc-shot-wrap {
    display: flex;
    justify-content: center;
  }
  .sc-shot {
    width: 100%;
    max-width: 300px;
    height: auto;
    border-radius: 22px;
    border: 1px solid var(--border-strong);
    box-shadow: 0 24px 48px -22px rgba(21, 12, 43, 0.6);
    background: var(--sc-deep);
  }

  .sc-shot-trio {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0.85rem;
  }
  .sc-shot-trio .sc-shot {
    max-width: 100%;
  }

  .sc-band {
    background: var(--surface-2);
    border-top: 1px solid var(--border);
    border-bottom: 1px solid var(--border);
  }

  .sc-content {
    padding: clamp(1.5rem, 4vw, 2.75rem);
    text-align: center;
  }
  .sc-content p {
    color: var(--text-muted);
    margin: 0;
    max-width: 72ch;
    margin-left: auto;
    margin-right: auto;
  }

  /* ---------- SERVICES ---------- */
  .sc-services {
    text-align: center;
  }
  .sc-services-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(1.9rem, 4.4vw, 2.9rem);
    margin-bottom: 1.5rem;
  }
  .sc-services-list {
    list-style: none;
    padding: 0;
    margin: 0 auto 1.5rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
    max-width: 900px;
  }
  .sc-services-list li {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 999px;
    padding: 0.55rem 1.15rem;
    color: var(--text-muted);
    font-size: 0.95rem;
  }
  .sc-services p {
    color: var(--text-muted);
    margin: 0;
  }

  /* ---------- TELECHARGEMENT ---------- */
  .sc-actions {
    padding: clamp(2.5rem, 6vw, 4.5rem) 0 clamp(1.5rem, 4vw, 3rem);
  }
  .sc-actions-inner {
    text-align: center;
  }
  .sc-download-title {
    font-family: 'fightt', sans-serif;
    font-size: clamp(2rem, 5vw, 3.2rem);
    margin-bottom: 0.5rem;
  }
  .sc-download-sub {
    color: var(--text-muted);
    margin-bottom: 1.5rem;
  }
  .sc-buttons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }

  .gplay-badge img {
    height: 76px;
    width: auto;
    transition: transform 0.2s ease;
  }
  .gplay-badge:hover img {
    transform: translateY(-3px) scale(1.03);
  }

  .btn-quiet {
    display: inline-flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.85rem 1.5rem;
    border-radius: 999px;
    border: 1px solid var(--border-strong);
    color: var(--text);
    text-decoration: none;
    font-family: 'agency', sans-serif;
    font-size: 1.1rem;
    letter-spacing: 1px;
    text-transform: uppercase;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  }
  .btn-quiet:hover,
  .btn-quiet:focus {
    border-color: var(--sc-violet);
    background: rgba(124, 58, 237, 0.10);
    color: var(--text);
    text-decoration: none;
    transform: translateY(-2px);
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 850px) {
    .sc-feature,
    .sc-feature-reverse {
      grid-template-columns: 1fr;
      text-align: center;
    }
    .sc-feature-text {
      text-align: center;
    }
    .sc-feature-reverse .sc-feature-text {
      order: -1;
    }
    .sc-shot-trio {
      gap: 0.5rem;
    }
  }
</style>
